import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

const serviceLinks = [
  {
    label: "Immigration Law",
    href: "/services/immigration-law",
    submenu: [
      {
        label: "Partner and Family Visa",
        href: "/services/partner-and-family-visa",
      },
      {
        label: "ELA Nationals & Family",
        href: "/services/immigration-law/ela-nationals-family",
      },
      {
        label: "Short Stay Visit Visas",
        href: "/services/immigration-law/short-stay-visit-visas",
      },
      { label: "Work Visas", href: "/services/immigration-law/work-visas" },
      {
        label: "Business Immigration",
        href: "/services/immigration-law/business-immigration",
      },
      {
        label: "Student & Graduate Visas",
        href: "/services/immigration-law/student-graduate-visas",
      },
      {
        label: "Settlement in the UK",
        href: "/services/immigration-law/settlement-uk",
      },
      {
        label: "British Citizenship",
        href: "/services/immigration-law/british-citizenship",
      },
      { label: "Human Rights", href: "/services/immigration-law/human-rights" },
      {
        label: "Asylum and Appeal",
        href: "/services/immigration-law/asylum-appeal",
      },
    ],
  },
  {
    label: "Family Law",
    href: "/services/family-law",
    submenu: [
      { label: "Divorces", href: "/services/family-law/divorces" },
      { label: "Children Law", href: "/services/family-law/children-law" },
      {
        label: "Cohabitation Matters",
        href: "/services/family-law/cohabitation-matters",
      },
      {
        label: "Civil Partnerships & Agreements",
        href: "/services/family-law/civil-partnerships-agreements",
      },
    ],
  },
];

const socialLinks = [
  {
    label: "LinkedIn",
    href: "https://www.linkedin.com",
    icon: (
      <path d="M6.94 8.5A1.56 1.56 0 1 0 6.94 5.38a1.56 1.56 0 0 0 0 3.12ZM5.5 9.5h2.88V18H5.5zM10.5 9.5h2.76v1.16h.04c.38-.72 1.31-1.48 2.7-1.48 2.88 0 3.41 1.89 3.41 4.35V18h-2.88v-7.5c0-1.79-.03-4.09-2.49-4.09-2.5 0-2.88 1.95-2.88 3.96V18H10.5z" />
    ),
  },
  {
    label: "Instagram",
    href: "https://www.instagram.com",
    icon: (
      <path d="M7 3h10a4 4 0 0 1 4 4v10a4 4 0 0 1-4 4H7a4 4 0 0 1-4-4V7a4 4 0 0 1 4-4Zm0 2a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2H7Zm5 2.25A4.75 4.75 0 1 1 7.25 12 4.75 4.75 0 0 1 12 7.25Zm0 2A2.75 2.75 0 1 0 14.75 12 2.75 2.75 0 0 0 12 9.25Zm4.75-3.25a1.25 1.25 0 1 1-1.25 1.25 1.25 1.25 0 0 1 1.25-1.25Z" />
    ),
  },
  {
    label: "Facebook",
    href: "https://www.facebook.com",
    icon: (
      <path d="M13.5 21v-8h2.7l.4-3h-3.1V3.9c0-.87.24-1.46 1.49-1.46H17V.1c-.26-.03-1.15-.1-2.18-.1-2.16 0-3.64 1.32-3.64 3.74V10H8.8v3h2.38v8h2.32Z" />
    ),
  },
];

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [activeServiceSubmenu, setActiveServiceSubmenu] = useState(null);
  const [hasScrolled, setHasScrolled] = useState(false);
  const servicesMenuRef = useRef(null);

  useEffect(() => {
    const handleDocumentClick = (event) => {
      if (
        servicesMenuRef.current &&
        !servicesMenuRef.current.contains(event.target)
      ) {
        setServicesOpen(false);
        setActiveServiceSubmenu(null);
      }
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setServicesOpen(false);
        setActiveServiceSubmenu(null);
      }
    };

    document.addEventListener("mousedown", handleDocumentClick);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handleDocumentClick);
      document.removeEventListener("keydown", handleEscape);
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setHasScrolled(window.scrollY > 24);
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  return (
    <header
      className={`fixed left-0 top-0 z-50 w-full transition-colors duration-300 ${hasScrolled
        ? "bg-[rgba(26,36,58,0.86)] backdrop-blur-sm"
        : "bg-[rgb(26,36,58)]"
        }`}
    >
      <div className="container relative mx-auto py-3 lg:py-4">
        <div
          className={`flex items-center justify-between overflow-hidden transition-all duration-300 ${hasScrolled ? "max-h-0 opacity-0 py-0" : "max-h-24 opacity-100 py-2"
            }`}
        >
          <Link to="/" className="inline-flex items-center">
            <img
              src="/BlackHorseLogo.png"
              alt="Blackhorse Law"
              className="h-14 w-auto lg:h-16"
            />
          </Link>

          <div className="flex items-center gap-3">
            {socialLinks.map((link) => (
              <a
                key={link.label}
                href={link.href}
                target="_blank"
                rel="noreferrer"
                aria-label={link.label}
                className="flex h-10 w-10 items-center justify-center rounded-full border border-white/20 bg-white/10 text-white transition hover:border-[#C9A04A] hover:text-[#C9A04A]"
              >
                <svg className="h-5 w-5 fill-current" viewBox="0 0 24 24">
                  {link.icon}
                </svg>
              </a>
            ))}
          </div>
        </div>

        <div className="border-t border-white/10 pt-3 lg:pt-4">
          <div className="flex items-center justify-between gap-4">
            <button
              className="lg:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              <svg
                className="h-6 w-6"
                fill="none"
                stroke="white"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              </svg>
            </button>

            <nav
              className={`${mobileMenuOpen ? "flex" : "hidden"
                } absolute left-0 top-full w-full flex-col gap-4 rounded-b-2xl border border-white/10 bg-[rgba(26,36,58,0.97)] p-4 text-white shadow-lg lg:static lg:flex lg:flex-row lg:items-center lg:justify-center lg:gap-8 lg:rounded-none lg:border-0 lg:bg-transparent lg:p-0 lg:shadow-none`}
            >
              <div>
                <Link to="/#about" className="hover:opacity-80">
                  About
                </Link>
              </div>
              <div
                ref={servicesMenuRef}
                className="relative lg:-my-4 lg:py-4"
                onMouseEnter={() => setServicesOpen(true)}
                onMouseLeave={() => {
                  setServicesOpen(false);
                  setActiveServiceSubmenu(null);
                }}
              >
                <button
                  type="button"
                  className="font-inherit text-left hover:opacity-80 focus:outline-none focus:ring-2 focus:ring-white/70 focus:ring-offset-2 focus:ring-offset-purple lg:focus:ring-offset-transparent"
                  aria-expanded={servicesOpen}
                  aria-haspopup="true"
                  onClick={() => {
                    setServicesOpen((open) => {
                      if (open) {
                        setActiveServiceSubmenu(null);
                      }

                      return !open;
                    });
                  }}
                >
                  Services
                </button>

                <div
                  className={`${servicesOpen ? "block" : "hidden"} mt-4 w-full rounded-lg border border-[#C9A04A] bg-blue-custom px-3 py-3 text-white shadow-[0_10px_24px_rgba(255,215,0,0.22)] lg:absolute lg:left-1/2 lg:top-full lg:mt-0 lg:w-52 lg:-translate-x-1/2`}
                >
                  <span className="absolute -top-2 left-1/2 hidden h-4 w-4 -translate-x-1/2 rotate-45 border-l border-t border-[#C9A04A] bg-[#001f3f] lg:block" />
                  <ul className="relative space-y-1 text-sm font-medium">
                    {serviceLinks.map((service) => (
                      <li
                        key={service.label}
                        className="relative"
                        onMouseEnter={() =>
                          setActiveServiceSubmenu(service.label)
                        }
                      >
                        <button
                          type="button"
                          className={`block w-full rounded-md border px-2 py-1.5 text-left hover:border-[#C9A04A] hover:text-[#C9A04A] focus:outline-none focus:ring-1 focus:ring-yellow/70 ${activeServiceSubmenu === service.label
                            ? "border-[#C9A04A] text-[#C9A04A]"
                            : "border-transparent"
                            }`}
                          aria-expanded={activeServiceSubmenu === service.label}
                          aria-haspopup="true"
                          onClick={() =>
                            setActiveServiceSubmenu((active) =>
                              active === service.label ? null : service.label,
                            )
                          }
                        >
                          <span>{service.label}</span>
                          <span
                            aria-hidden="true"
                            className="hidden text-base leading-none"
                          >
                            ›
                          </span>
                        </button>

                        <div
                          className={`${activeServiceSubmenu === service.label ? "block" : "hidden"} mt-2 rounded-lg border border-[#C9A04A] bg-blue-custom px-3 py-3 text-white shadow-[0_10px_24px_rgba(255,215,0,0.22)] lg:absolute lg:left-full lg:top-0 lg:mt-0 lg:ml-4 lg:w-52`}
                        >
                          <span className="absolute -left-2 top-4 hidden h-4 w-4 rotate-45 border-b border-l border-[#C9A04A] bg-[#001f3f] lg:block" />
                          <ul className="relative space-y-1">
                            {service.submenu.map((submenuItem) => (
                              <li key={submenuItem.label}>
                                <Link
                                  to={submenuItem.href}
                                  className="block rounded-md border border-transparent px-2 py-1.5 hover:border-[#C9A04A] hover:text-[#C9A04A] focus:outline-none focus:ring-1 focus:ring-yellow/70"
                                  onClick={() => {
                                    setServicesOpen(false);
                                    setActiveServiceSubmenu(null);
                                    setMobileMenuOpen(false);
                                  }}
                                >
                                  {submenuItem.label}
                                </Link>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              <div>
                <Link
                  to="/our-people"
                  className="hover:opacity-80"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Our People
                </Link>
              </div>
              {/* <div><Link to="/#news" className="hover:opacity-80">News</Link></div> */}
              <div>
                <Link
                  to="/contact"
                  className="hover:opacity-80"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Contact
                </Link>
              </div>
              <div
                className={`${hasScrolled ? "flex" : "hidden"} lg:ml-6 items-center justify-center`}
              >
                <img
                  src="/favicon.ico"
                  alt="Blackhorse favicon"
                  className="h-8 w-8"
                />
              </div>
            </nav>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
